import { NextTriggeredBy, PaymentMethodSaveMode } from '@/components/Invoice/types';
import { createElement, Payment } from '@airwallex/components-sdk';

export const PAY_EVENT_CODES = [
  'onRedirect',
  'onApplePayButtonClick',
  'onGooglePayButtonClick',
  'onClickConfirmButton',
];

export type IPaymentElement = {
  intent_id?: string;
  client_secret: string;
  currency: string;
  countryCode?: string;
  merchantCountryCode?: string;
  shopper_name?: string;
  shopper_phone?: string;
  shopper_email?: string;
  customer_id?: string;
  shouldCreateMITConsent?: boolean;
  disablePay?: boolean;
  paymentMethods: Payment.PaymentMethodType[];
  billing?: Payment.Billing;
  paymentMethodSave?: {
    mode: PaymentMethodSaveMode;
    nextTriggeredBy?: NextTriggeredBy;
  };
  gaOrderId?: string;
};

export const getFirstAndLastName = (name: string) => {
  const [firstName, ...lastName] = name?.split(' ') || [];
  return { firstName, lastName: lastName.join(' ') };
};

const SAVE_MODE_MAP: Record<PaymentMethodSaveMode, Payment.SaveMode> = {
  ENABLED: 'enable',
  DISABLED: 'disable',
  COLLECT_CONSENT: 'collect_consent',
};

function validatePaymentMethodSaveConsent(paymentMethodSave: IPaymentElement['paymentMethodSave']): void {
  if (!paymentMethodSave) return;
  if (paymentMethodSave.mode === 'DISABLED' && paymentMethodSave.nextTriggeredBy != null) {
    throw new Error('nextTriggeredBy must not be set when payment method save mode is DISABLED');
  }
  if (
    (paymentMethodSave.mode === 'ENABLED' || paymentMethodSave.mode === 'COLLECT_CONSENT') &&
    paymentMethodSave.nextTriggeredBy != null
  ) {
    const next = paymentMethodSave.nextTriggeredBy.toLowerCase();
    if (next !== 'merchant' && next !== 'customer') {
      throw new Error(
        'nextTriggeredBy must be MERCHANT or CUSTOMER when payment method save is ENABLED or COLLECT_CONSENT'
      );
    }
  }
}

function buildPaymentConsent(
  paymentMethodSave: IPaymentElement['paymentMethodSave'],
  shouldCreateMITConsent?: boolean
): { next_triggered_by: 'merchant' | 'customer' } | undefined {
  if (paymentMethodSave?.nextTriggeredBy) {
    return { next_triggered_by: paymentMethodSave.nextTriggeredBy.toLowerCase() as 'merchant' | 'customer' };
  }
  if (shouldCreateMITConsent) {
    return { next_triggered_by: 'merchant' };
  }
  return undefined;
}

export const generateDropInElement = ({
  intent_id,
  client_secret,
  currency,
  countryCode,
  merchantCountryCode,
  shopper_name,
  shopper_phone,
  shopper_email,
  customer_id,
  shouldCreateMITConsent,
  disablePay,
  paymentMethods,
  billing,
  paymentMethodSave,
  gaOrderId,
}: IPaymentElement) => {
  validatePaymentMethodSaveConsent(paymentMethodSave);
  const options: Payment.DropInElementOptions = {
    mode: shouldCreateMITConsent ? 'recurring' : 'payment',
    intent_id,
    client_secret,
    currency,
    autoCapture: true,
    requiredBillingContactFields: ['name'],
    shopper_name,
    shopper_phone,
    shopper_email,
    savedPaymentMethod: shouldCreateMITConsent
      ? undefined
      : {
          saveMode: paymentMethodSave ? SAVE_MODE_MAP[paymentMethodSave.mode] ?? 'disable' : 'disable',
        },
    payment_consent: buildPaymentConsent(paymentMethodSave, shouldCreateMITConsent),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    disablePay,
    country_code: countryCode,
    customer_id: shouldCreateMITConsent ? customer_id : undefined,
    methods: paymentMethods,
    submitType: 'pay',
    billing,
    googlePayRequestOptions: merchantCountryCode
      ? {
          countryCode: merchantCountryCode,
          billingAddressRequired: false,
          billingAddressParameters: {
            format: 'MIN',
          },
          emailRequired: !shopper_email,
          buttonType: 'plain',
          buttonColor: 'black',
        }
      : undefined,
    applePayRequestOptions: merchantCountryCode
      ? {
          countryCode: merchantCountryCode,
          requiredShippingContactFields: [
            shopper_name ? undefined : 'name',
            shopper_email ? undefined : 'email',
          ].filter(Boolean) as ApplePayJS.ApplePayContactField[],
          buttonType: 'plain',
          buttonColor: 'black',
        }
      : undefined,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    layout: {
      type: 'accordion',
      alwaysShowMethodLabel: true,
    },
    style: {
      variant: 'bootstrap',
      popupWidth: 400,
      popupHeight: 549,
      base: {
        color: 'black',
      },
    },
  };
  if (gaOrderId) {
    // @ts-expect-error Extended drop-in option: payment_method.bank_transfer.ga_order_id not on DropInElementOptions type
    options.payment_method = {
      bank_transfer: {
        ga_mode: 'PAYER_GA',
        ga_order_id: gaOrderId,
      },
    };
  }
  return createElement('dropIn', options);
};
